import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Leaf,
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  LogOut,
  Image,
  X,
  FileText,
  Upload,
} from "lucide-react";
import { ImageUploader } from "@/components/ImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const auth = sessionStorage.getItem("adminAuth");
  if (!auth) return null;
  return { Authorization: `Basic ${auth}` };
};

export const AdminProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryMode, setCategoryMode] = useState("add");
  const [categoryForm, setCategoryForm] = useState({ id: "", name: "", description: "", image_url: "" });
  const [settings, setSettings] = useState({ low_stock_threshold: 5, auto_out_of_stock: true });
  const [uploadingDigital, setUploadingDigital] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    image_url: "",
    images: [],
    product_type: "physical",
    digital_file: null,
    sku: "",
    inventory: "0",
    weight_oz: "16",
    length_in: "10",
    width_in: "8",
    height_in: "4",
    in_stock: true,
    is_featured: false,
    is_new: false,
  });

  const headers = getAuthHeaders();

  useEffect(() => {
    if (!headers) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/settings`, { headers }),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSettings({
        low_stock_threshold: settingsRes.data.low_stock_threshold ?? 5,
        auto_out_of_stock: settingsRes.data.auto_out_of_stock ?? true,
      });
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(
        `${API}/admin/settings`,
        {
          low_stock_threshold: parseInt(settings.low_stock_threshold, 10) || 0,
          auto_out_of_stock: settings.auto_out_of_stock,
        },
        { headers }
      );
      toast.success("Inventory settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const uploadDigitalFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingDigital(true);
    try {
      const res = await axios.post(`${API}/admin/upload-digital`, fd, { headers });
      setForm((f) => ({ ...f, digital_file: res.data }));
      toast.success("Digital file uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingDigital(false);
      e.target.value = "";
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      price: "",
      category: "",
      image_url: "",
      images: [],
      product_type: "physical",
      digital_file: null,
      sku: "",
      inventory: "0",
      weight_oz: "16",
      length_in: "10",
      width_in: "8",
      height_in: "4",
      in_stock: true,
      is_featured: false,
      is_new: false,
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  const handleEdit = (product) => {
    const imgs =
      product.images && product.images.length > 0
        ? product.images
        : product.image_url
        ? [product.image_url]
        : [];
    setForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url,
      images: imgs,
      product_type: product.product_type || "physical",
      digital_file: product.digital_file || null,
      sku: product.sku || "",
      inventory: (product.inventory ?? 0).toString(),
      weight_oz: (product.weight_oz || 16).toString(),
      length_in: (product.length_in || 10).toString(),
      width_in: (product.width_in || 8).toString(),
      height_in: (product.height_in || 4).toString(),
      in_stock: product.in_stock,
      is_featured: product.is_featured,
      is_new: product.is_new,
    });
    setEditingProduct(product);
    setShowForm(true);
  };

  const addImage = (url) => {
    if (!url) return;
    setForm((f) => ({ ...f, images: [...f.images, url] }));
    setUploaderKey((k) => k + 1);
  };

  const removeImage = (index) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== index) }));
  };

  const moveImageToFront = (index) => {
    setForm((f) => {
      const imgs = [...f.images];
      const [picked] = imgs.splice(index, 1);
      return { ...f, images: [picked, ...imgs] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.category) {
      toast.error("Please fill in all required fields");
      return;
    }

    const productData = {
      ...form,
      price: parseFloat(form.price),
      images: form.images,
      image_url: form.images[0] || form.image_url || "",
      product_type: form.product_type,
      digital_file: form.product_type === "digital" ? form.digital_file : null,
      sku: form.sku,
      inventory: parseInt(form.inventory, 10) || 0,
      weight_oz: parseFloat(form.weight_oz) || 16,
      length_in: parseFloat(form.length_in) || 10,
      width_in: parseFloat(form.width_in) || 8,
      height_in: parseFloat(form.height_in) || 4,
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, { headers });
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/admin/products`, productData, { headers });
        toast.success("Product created!");
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save product");
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers });
      toast.success("Product deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  const openAddCategory = () => {
    setCategoryMode("add");
    setCategoryForm({ id: "", name: "", description: "", image_url: "" });
    setShowCategoryForm(true);
  };

  const openEditCategory = () => {
    const cat = categories.find((c) => c.slug === form.category);
    if (!cat) {
      toast.error("Select a category to edit first");
      return;
    }
    setCategoryMode("edit");
    setCategoryForm({ id: cat.id, name: cat.name, description: cat.description || "", image_url: cat.image_url || "" });
    setShowCategoryForm(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    try {
      if (categoryMode === "edit") {
        const res = await axios.put(
          `${API}/admin/categories/${categoryForm.id}`,
          { name: categoryForm.name, description: categoryForm.description, image_url: categoryForm.image_url },
          { headers }
        );
        toast.success("Category updated!");
        const updated = res.data;
        setForm((f) => ({ ...f, category: updated.slug }));
      } else {
        const res = await axios.post(
          `${API}/admin/categories`,
          { name: categoryForm.name, description: categoryForm.description, image_url: categoryForm.image_url },
          { headers }
        );
        toast.success("Category added!");
        setForm((f) => ({ ...f, category: res.data.slug }));
      }
      setShowCategoryForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    }
  };

  const deleteCategory = async () => {
    const cat = categories.find((c) => c.slug === form.category);
    if (!cat) {
      toast.error("Select a category to delete first");
      return;
    }
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${cat.id}`, { headers });
      toast.success("Category deleted!");
      setForm((f) => ({ ...f, category: "" }));
      setShowCategoryForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete category");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-cream animate-fade-in">
      {/* Header */}
      <header className="bg-deep-moss text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Leaf className="w-6 h-6" />
              <span className="font-serif text-xl font-semibold">WEH Admin</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 font-sans text-sm text-white/80 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            data-testid="back-to-dashboard"
            className="text-stone-500 hover:text-deep-moss"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-serif text-3xl font-semibold text-stone-900">Products</h1>
        </div>

        {/* Inventory Settings */}
        <div className="bg-white p-5 border border-stone-200 mb-6" data-testid="inventory-settings">
          <h2 className="font-serif text-lg font-semibold text-stone-900 mb-4">Inventory Settings</h2>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div>
              <label className="block font-sans text-sm text-stone-700 mb-1">Low-stock threshold</label>
              <input
                type="number"
                min="0"
                step="1"
                value={settings.low_stock_threshold}
                onChange={(e) => setSettings({ ...settings, low_stock_threshold: e.target.value })}
                data-testid="low-stock-threshold-input"
                className="w-40 border border-stone-200 rounded px-3 py-2 font-sans text-sm"
              />
              <p className="font-sans text-xs text-stone-400 mt-1">Flag products at or below this quantity</p>
            </div>
            <label className="flex items-center gap-2 font-sans text-sm text-stone-700 pb-2">
              <input
                type="checkbox"
                checked={settings.auto_out_of_stock}
                onChange={(e) => setSettings({ ...settings, auto_out_of_stock: e.target.checked })}
                data-testid="auto-oos-checkbox"
                className="rounded"
              />
              Automatically mark items "Out of Stock" when inventory hits 0
            </label>
            <button
              type="button"
              onClick={saveSettings}
              data-testid="save-settings-btn"
              className="bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-6 py-2 font-sans text-sm font-medium sm:ml-auto"
            >
              Save Settings
            </button>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm ? (
          <div className="bg-white p-6 border border-stone-200 mb-8 animate-fade-in">
            <h2 className="font-serif text-xl font-semibold text-stone-900 mb-6">
              {editingProduct ? "Edit Product" : "Add New Product"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="product-name-input"
                    className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-1">Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    data-testid="product-price-input"
                    className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block font-sans text-sm text-stone-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="product-desc-input"
                  rows={3}
                  className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                />
              </div>
              {/* Product Type */}
              <div>
                <label className="block font-sans text-sm text-stone-700 mb-1">Product Type *</label>
                <select
                  value={form.product_type}
                  onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                  data-testid="product-type-select"
                  className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                >
                  <option value="physical">Physical (shipped)</option>
                  <option value="digital">Digital (instant download — ebook, PDF, etc.)</option>
                </select>
              </div>
              {form.product_type === "digital" && (
                <div className="bg-deep-moss/5 border border-deep-moss/20 rounded p-4" data-testid="digital-file-section">
                  <p className="font-sans text-sm font-medium text-stone-700 mb-2">
                    Digital File <span className="text-stone-400 font-normal">(sent automatically to the buyer after purchase)</span>
                  </p>
                  {form.digital_file ? (
                    <div className="flex items-center justify-between bg-white border border-stone-200 rounded px-3 py-2" data-testid="digital-file-info">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-deep-moss shrink-0" />
                        <span className="font-sans text-sm text-stone-700 truncate">{form.digital_file.name}</span>
                        <span className="font-sans text-xs text-stone-400 shrink-0">
                          ({form.digital_file.size < 1024 * 1024
                            ? `${Math.max(1, Math.round(form.digital_file.size / 1024))} KB`
                            : `${(form.digital_file.size / 1024 / 1024).toFixed(2)} MB`})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, digital_file: null })}
                        data-testid="remove-digital-file-btn"
                        className="text-red-500 hover:text-red-600 shrink-0 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer bg-white border border-dashed border-stone-300 rounded px-3 py-3 hover:border-deep-moss transition-colors">
                      <Upload className="w-4 h-4 text-stone-500" />
                      <span className="font-sans text-sm text-stone-600">
                        {uploadingDigital ? "Uploading..." : "Upload file (PDF, EPUB, MOBI, ZIP, MP3… max 50MB)"}
                      </span>
                      <input
                        type="file"
                        onChange={uploadDigitalFile}
                        disabled={uploadingDigital}
                        data-testid="digital-file-input"
                        className="hidden"
                        accept=".pdf,.epub,.mobi,.zip,.mp3,.m4a,.wav,.doc,.docx,.txt,.rtf,.mp4,.mov"
                      />
                    </label>
                  )}
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-1">Item Number / SKU</label>
                  <input
                    type="text"
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    data-testid="product-sku-input"
                    placeholder="e.g. WEH-MOR-001"
                    className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-1">Quantity / Inventory</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.inventory}
                    onChange={(e) => setForm({ ...form, inventory: e.target.value })}
                    data-testid="product-inventory-input"
                    className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  />
                </div>
              </div>
              {/* Shipping dimensions for USPS live rates (physical products only) */}
              {form.product_type !== "digital" && (
              <div className="bg-stone-50 border border-stone-200 rounded p-4">
                <p className="font-sans text-sm font-medium text-stone-700 mb-3">
                  Shipping Dimensions <span className="text-stone-400 font-normal">(used for USPS live rate calculation)</span>
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block font-sans text-xs text-stone-600 mb-1">Weight (oz)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.weight_oz}
                      onChange={(e) => setForm({ ...form, weight_oz: e.target.value })}
                      data-testid="product-weight-input"
                      className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs text-stone-600 mb-1">Length (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.length_in}
                      onChange={(e) => setForm({ ...form, length_in: e.target.value })}
                      data-testid="product-length-input"
                      className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs text-stone-600 mb-1">Width (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.width_in}
                      onChange={(e) => setForm({ ...form, width_in: e.target.value })}
                      data-testid="product-width-input"
                      className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs text-stone-600 mb-1">Height (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.height_in}
                      onChange={(e) => setForm({ ...form, height_in: e.target.value })}
                      data-testid="product-height-input"
                      className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                    />
                  </div>
                </div>
              </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block font-sans text-sm text-stone-700">Category *</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={openAddCategory}
                        data-testid="add-category-btn"
                        className="flex items-center gap-1 text-xs text-deep-moss hover:underline"
                      >
                        <Plus className="w-3 h-3" /> Add
                      </button>
                      <button
                        type="button"
                        onClick={openEditCategory}
                        data-testid="edit-category-btn"
                        className="flex items-center gap-1 text-xs text-stone-500 hover:text-deep-moss"
                      >
                        <Edit className="w-3 h-3" /> Edit
                      </button>
                    </div>
                  </div>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="product-category-select"
                    className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.slug} value={cat.slug}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {showCategoryForm && (
                    <div className="mt-3 p-4 bg-stone-50 border border-stone-200 rounded space-y-3 animate-fade-in" data-testid="category-form">
                      <p className="font-sans text-sm font-medium text-stone-700">
                        {categoryMode === "edit" ? "Edit Category" : "Add Category"}
                      </p>
                      <input
                        type="text"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                        data-testid="category-name-input"
                        placeholder="Category name"
                        className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                      />
                      <input
                        type="text"
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        data-testid="category-desc-input"
                        placeholder="Short description (optional)"
                        className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                      />
                      <div>
                        <label className="block font-sans text-xs text-stone-600 mb-1">Category Image</label>
                        <div data-testid="category-image-uploader">
                          <ImageUploader
                            currentImage={categoryForm.image_url}
                            onUpload={(url) => setCategoryForm((c) => ({ ...c, image_url: url }))}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={saveCategory}
                          data-testid="save-category-btn"
                          className="bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-4 py-1.5 font-sans text-xs font-medium"
                        >
                          {categoryMode === "edit" ? "Update" : "Add"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCategoryForm(false)}
                          data-testid="cancel-category-btn"
                          className="border border-stone-300 text-stone-700 rounded-full px-4 py-1.5 font-sans text-xs"
                        >
                          Cancel
                        </button>
                        {categoryMode === "edit" && (
                          <button
                            type="button"
                            onClick={deleteCategory}
                            data-testid="delete-category-btn"
                            className="ml-auto flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Product Images (multi-image gallery) */}
              <div>
                <label className="block font-sans text-sm text-stone-700 mb-1">
                  Product Images <span className="text-stone-400 font-normal">(first image is the cover shown on cards)</span>
                </label>
                {form.images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3" data-testid="product-images-grid">
                    {form.images.map((img, idx) => (
                      <div
                        key={`${img}-${idx}`}
                        data-testid={`product-image-thumb-${idx}`}
                        className={`relative aspect-square rounded overflow-hidden border ${
                          idx === 0 ? "border-deep-moss ring-2 ring-deep-moss/30" : "border-stone-200"
                        }`}
                      >
                        <img src={img} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" />
                        {idx === 0 ? (
                          <span className="absolute top-1 left-1 bg-deep-moss text-white text-[10px] px-1.5 py-0.5 rounded">
                            Cover
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => moveImageToFront(idx)}
                            data-testid={`set-cover-${idx}`}
                            title="Set as cover"
                            className="absolute bottom-1 left-1 bg-white/90 text-deep-moss text-[10px] px-1.5 py-0.5 rounded hover:bg-white"
                          >
                            Set cover
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          data-testid={`remove-image-${idx}`}
                          title="Remove image"
                          className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-50"
                        >
                          <X className="w-3 h-3 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <ImageUploader key={uploaderKey} onUpload={addImage} />
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 font-sans text-sm">
                  <input
                    type="checkbox"
                    checked={form.in_stock}
                    onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
                    className="rounded"
                  />
                  In Stock
                </label>
                <label className="flex items-center gap-2 font-sans text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_featured}
                    onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                    className="rounded"
                  />
                  Featured
                </label>
                <label className="flex items-center gap-2 font-sans text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_new}
                    onChange={(e) => setForm({ ...form, is_new: e.target.checked })}
                    className="rounded"
                  />
                  New
                </label>
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  data-testid="save-product-btn"
                  className="bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-6 py-2 font-sans text-sm font-medium"
                >
                  {editingProduct ? "Update Product" : "Add Product"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="cancel-btn"
                  className="border border-stone-300 text-stone-700 rounded-full px-6 py-2 font-sans text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : (
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-product-btn"
            className="flex items-center gap-2 bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-6 py-2 font-sans text-sm font-medium mb-8"
          >
            <Plus className="w-4 h-4" />
            Add New Product
          </button>
        )}

        {/* Products List */}
        <div className="bg-white border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Image</th>
                  <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">SKU</th>
                  <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Price</th>
                  <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right font-sans text-xs font-medium text-stone-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {products.map((product) => (
                  <tr key={product.id} data-testid={`product-row-${product.id}`}>
                    <td className="px-4 py-3">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-12 h-12 object-cover"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-sans text-sm font-medium text-stone-900">{product.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-sans text-xs text-stone-600" data-testid={`product-sku-${product.id}`}>
                        {product.sku || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-sans text-sm text-stone-600 capitalize">
                        {product.category.replace("-", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-sans text-sm font-medium text-stone-900">
                        ${product.price.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {product.in_stock ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">In Stock</span>
                        ) : (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Out of Stock</span>
                        )}
                        {product.is_featured && (
                          <span className="text-xs bg-deep-moss/10 text-deep-moss px-2 py-0.5 rounded">Featured</span>
                        )}
                        {product.is_new && (
                          <span className="text-xs bg-terracotta/10 text-terracotta px-2 py-0.5 rounded">New</span>
                        )}
                        <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded" data-testid={`product-inventory-${product.id}`}>
                          Qty: {product.inventory ?? 0}
                        </span>
                        {(product.inventory ?? 0) > 0 && (product.inventory ?? 0) <= settings.low_stock_threshold && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded" data-testid={`product-low-stock-${product.id}`}>
                            Low Stock
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="p-2 text-stone-500 hover:text-deep-moss"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-product-${product.id}`}
                          className="p-2 text-stone-500 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
